import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Avatar,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Skeleton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import InboxIcon from "@mui/icons-material/Inbox";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import listStyles from "./AllEnquiries.module.scss";
import styles from "./Universities.module.scss";
import {
  addUniversity,
  deleteUniversity,
  getUniversities,
  updateUniversity,
  searchInstitutionLogos,
  downloadUniversitiesExcelReport,
} from "../Components/enquiryProfileApi";
import { RoleContext } from "../Contexts/RoleContext";

const theme = createTheme({
  palette: { primary: { main: "#5e72e4" }, background: { default: "#f8f9fe" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", h4: { fontWeight: 600 }, button: { textTransform: "none" } },
  shape: { borderRadius: 12 },
});

function statusBadgeClass(status) {
  if (status === "approved") return styles.badgeApproved;
  if (status === "rejected") return styles.badgeRejected;
  return styles.badgePending;
}

function getUniversityStatusChipColor(status) {
  if (status === "approved") return "success";
  if (status === "rejected") return "error";
  return "warning";
}

export default function UniversitiesListPage() {
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);
  const [universities, setUniversities] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [logoOptions, setLogoOptions] = useState([]);
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    logo_url: "",
    website: "",
  });
  const [editForm, setEditForm] = useState({
    id: null,
    name: "",
    description: "",
    logo_url: "",
    website: "",
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const debouncedSearch = useDebouncedValue(search, 400);
  const [viewMode, setViewMode] = useListViewMode("universities", "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));

  const getDateRangeParams = useCallback(() => {
    const today = new Date();
    let start = null;
    let end = null;
    switch (dateRange) {
      case "today":
        start = end = new Date(today);
        break;
      case "yesterday":
        start = end = new Date(today);
        start.setDate(start.getDate() - 1);
        break;
      case "this_week":
        start = new Date(today);
        start.setDate(start.getDate() - start.getDay());
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        break;
      case "last_week":
        start = new Date(today);
        start.setDate(start.getDate() - start.getDay() - 7);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "this_year":
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      case "last_year":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "custom":
        if (customStart && customEnd) {
          start = new Date(customStart);
          end = new Date(customEnd);
        }
        break;
      case "all":
      default:
        return { date_from: undefined, date_to: undefined };
    }
    if (!start || !end) return { date_from: undefined, date_to: undefined };
    const toYMD = (d) => d.toISOString().slice(0, 10);
    return { date_from: toYMD(start), date_to: toYMD(end) };
  }, [dateRange, customStart, customEnd]);

  const load = useCallback(async () => {
    const { date_from, date_to } = getDateRangeParams();
    setLoading(true);
    try {
      const data = await getUniversities({
        search: debouncedSearch,
        status,
        includeUsage: true,
        date_from,
        date_to,
      });
      setUniversities(Array.isArray(data?.universities) ? data.universities : []);
    } catch (error) {
      toast.error(error.message || "Failed to load programs.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, getDateRangeParams]);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    try {
      await deleteUniversity(id);
      toast.success("Program deleted.");
      await load();
    } catch (error) {
      toast.error(error.message || "Delete failed.");
    }
  };

  useEffect(() => {
    if (!openAddModal) return;
    const name = createForm.name.trim();
    if (!name) {
      setLogoOptions([]);
      setLogoError("");
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingLogo(true);
      setLogoError("");
      try {
        const options = await searchInstitutionLogos(name);
        if (options.length === 0) {
          setLogoError("No matching institutions found.");
        }
        setLogoOptions(options);
      } catch (error) {
        setLogoOptions([]);
        setLogoError(error.message || "Error fetching institutions.");
      } finally {
        setLoadingLogo(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [createForm.name, openAddModal]);

  const handleSelectLogoOption = (_, value) => {
    if (!value) return;
    setCreateForm((prev) => ({
      ...prev,
      name: value.name || prev.name,
      logo_url: value.logo_url || prev.logo_url,
      website: value.domain || prev.website,
    }));
  };

  const handleOpenAddUniversity = () => {
    setCreateForm({ name: "", description: "", logo_url: "", website: "" });
    setLogoOptions([]);
    setLogoError("");
    setOpenAddModal(true);
  };

  const onCreateUniversity = async () => {
    if (!createForm.name.trim()) {
      toast.warning("Program name is required.");
      return;
    }
    setSaving(true);
    try {
      await addUniversity({
        name: createForm.name.trim(),
        description: (createForm.description || "").trim() || null,
        logo_url: createForm.logo_url || null,
        website: createForm.website || null,
      });
      toast.success("Program added successfully.");
      setOpenAddModal(false);
      await load();
    } catch (error) {
      toast.error(error.message || "Failed to add program.");
    } finally {
      setSaving(false);
    }
  };

  const onOpenEditProgram = (program) => {
    setEditForm({
      id: program.id,
      name: program.name || "",
      description: program.description || "",
      logo_url: program.logo_url || "",
      website: program.website || "",
    });
    setOpenEditModal(true);
  };

  const onUpdateUniversity = async () => {
    if (!editForm.id) return;
    if (!editForm.name.trim()) {
      toast.warning("Program name is required.");
      return;
    }
    setSaving(true);
    try {
      await updateUniversity(editForm.id, {
        name: editForm.name.trim(),
        description: (editForm.description || "").trim() || null,
        logo_url: (editForm.logo_url || "").trim() || null,
        website: (editForm.website || "").trim() || null,
      });
      toast.success("Program updated.");
      setOpenEditModal(false);
      await load();
    } catch (error) {
      toast.error(error.message || "Failed to update program.");
    } finally {
      setSaving(false);
    }
  };

  const onExportExcelReport = async () => {
    const { date_from, date_to } = getDateRangeParams();
    setExportingReport(true);
    try {
      await downloadUniversitiesExcelReport({ date_from, date_to });
      toast.success("Excel report downloaded.");
    } catch (error) {
      toast.error(error.message || "Failed to download report.");
    } finally {
      setExportingReport(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={listStyles.root}>
        <Grid container spacing={0}>
          <Grid item xs={12} className={listStyles.mainContent}>
            <Paper elevation={0} className={listStyles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="h4" className={listStyles.pageTitle}>
                      Programs
                    </Typography>
                    <Typography variant="body2" className={listStyles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Create programs and add sessions (fees, duration, type).
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className={listStyles.header}>
                <GlobalSearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Search program..."
                />
                <Box className={listStyles.headerActions}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(!filterOpen)}
                    sx={{ textTransform: "none" }}
                  >
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                  <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                    <IconButton onClick={toggleViewMode} color="primary">
                      {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                    </IconButton>
                  </Tooltip>
                  {role === "admin" && (
                    <Button
                      variant="outlined"
                      startIcon={exportingReport ? <CircularProgress size={18} /> : <TableChartIcon />}
                      disabled={exportingReport}
                      onClick={onExportExcelReport}
                      sx={{ textTransform: "none" }}
                    >
                      {exportingReport ? "Exporting…" : "Export Excel Report"}
                    </Button>
                  )}
                  <Button variant="contained" onClick={handleOpenAddUniversity}>
                    Add Program
                  </Button>
                </Box>
              </Box>

              {filterOpen && (
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                      <MenuItem value="all">All statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Date range</InputLabel>
                    <Select
                      value={dateRange}
                      label="Date range"
                      onChange={(e) => setDateRange(e.target.value)}
                    >
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="yesterday">Yesterday</MenuItem>
                      <MenuItem value="this_week">This Week</MenuItem>
                      <MenuItem value="last_week">Last Week</MenuItem>
                      <MenuItem value="this_month">This Month</MenuItem>
                      <MenuItem value="last_month">Last Month</MenuItem>
                      <MenuItem value="this_year">This Year</MenuItem>
                      <MenuItem value="last_year">Last Year</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="all">All time</MenuItem>
                    </Select>
                  </FormControl>
                  {dateRange === "custom" && (
                    <>
                      <TextField
                        size="small"
                        label="Start date"
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                      />
                      <TextField
                        size="small"
                        label="End date"
                        type="date"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                      />
                    </>
                  )}
                  <Button variant="contained" onClick={load}>
                    Apply
                  </Button>
                </Box>
              )}

              {loading && (
                <Box className={listStyles.loadingContainer}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                </Box>
              )}

              {!loading && universities.length === 0 && (
                <Box className={listStyles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No universities found.
                  </Typography>
                </Box>
              )}

              {!loading && universities.length > 0 && (
                <>
                  {viewMode === "cards" ? (
                    <Grid container spacing={2} className={listStyles.cardsGrid}>
                      {universities.map((uni) => (
                        <Grid item xs={12} sm={6} md={4} key={uni.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              overflow: "hidden",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                            }}
                          >
                            <CardHeader
                              avatar={
                                uni.logo_url ? (
                                  <Avatar src={uni.logo_url} alt={uni.name} sx={{ width: 48, height: 48 }} />
                                ) : (
                                  <Avatar sx={{ width: 48, height: 48, bgcolor: "grey.400" }}>N/A</Avatar>
                                )
                              }
                              title={uni.name}
                              subheader={<span className={statusBadgeClass(uni.status)}>{uni.status}</span>}
                              className={listStyles.cardHeader}
                            />
                            <CardContent className={listStyles.cardContent}>
                              <Typography variant="body2">
                                <strong>Created:</strong> {String(uni.created_at || "").slice(0, 10)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Usage:</strong> {uni.usage_count || 0}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => navigate(`/universities/program/${uni.id}`)}
                                >
                                  Manage sessions
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => onOpenEditProgram(uni)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => onDelete(uni.id)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Paper variant="outlined" className={listStyles.tableContainer}>
                      <TableContainer>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Logo</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Created Date</TableCell>
                              <TableCell>Usage Count</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {universities.map((uni) => (
                              <TableRow key={uni.id} hover className={listStyles.tableRow}>
                                <TableCell>
                                  {uni.logo_url ? (
                                    <img className={styles.uniLogo} src={uni.logo_url} alt={uni.name} />
                                  ) : (
                                    <div className={styles.uniLogoPlaceholder}>N/A</div>
                                  )}
                                </TableCell>
                                <TableCell className={listStyles.primaryText}>{uni.name}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={uni.status || "—"}
                                    color={getUniversityStatusChipColor(uni.status)}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell className={listStyles.secondaryText}>
                                  {String(uni.created_at || "").slice(0, 10)}
                                </TableCell>
                                <TableCell>{uni.usage_count || 0}</TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => navigate(`/universities/program/${uni.id}`)}
                                    >
                                      Manage sessions
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => onOpenEditProgram(uni)}
                                    >
                                      Edit
                                    </Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => onDelete(uni.id)}>
                                      Delete
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Dialog
            open={openAddModal}
            onClose={() => setOpenAddModal(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: 6,
                p: 1,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 600,
                fontSize: "1.3rem",
                pb: 1,
                color: "primary.main",
                textAlign: "center",
              }}
            >
              Add Program
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Program Details
              </Typography>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Autocomplete
                  fullWidth
                  freeSolo
                  loading={loadingLogo}
                  options={logoOptions}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={handleSelectLogoOption}
                  value={{ name: createForm.name }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Program Name"
                      onChange={(e) =>
                        setCreateForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLogo ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        borderRadius: 1,
                        "&:hover": { bgcolor: "grey.100" },
                      }}
                    >
                      <Avatar src={option.logo_url} alt={option.name} sx={{ width: 32, height: 32 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.domain}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />

                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label="Description"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Optional program description"
                  multiline
                  rows={2}
                />
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label="Website"
                  value={createForm.website}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, website: e.target.value }))
                  }
                  placeholder="example.edu"
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 2,
                    minHeight: 70,
                    p: 1,
                    borderRadius: 2,
                    border: "1px dashed #ccc",
                    bgcolor: "#fafafa",
                    mt: 2,
                  }}
                >
                  {loadingLogo ? (
                    <CircularProgress size={28} />
                  ) : createForm.logo_url ? (
                    <>
                      <Avatar
                        src={createForm.logo_url}
                        alt="Program Logo"
                        sx={{
                          width: 60,
                          height: 60,
                          border: "2px solid #ddd",
                          boxShadow: 1,
                        }}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {createForm.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Logo detected from logo.dev
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography color="error" variant="body2">
                      {logoError || "Type or select a program to detect logo"}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => setOpenAddModal(false)}>
                Cancel
              </Button>
              <Button onClick={onCreateUniversity} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
      <Dialog
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Edit Program</DialogTitle>
            <DialogContent sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Program Name"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Website"
                value={editForm.website}
                onChange={(e) => setEditForm((prev) => ({ ...prev, website: e.target.value }))}
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Logo URL"
                value={editForm.logo_url}
                onChange={(e) => setEditForm((prev) => ({ ...prev, logo_url: e.target.value }))}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => setOpenEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={onUpdateUniversity} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
    </ThemeProvider>
  );
}
